import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTheme, alpha } from "@mui/material/styles";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import StyleOutlinedIcon from "@mui/icons-material/StyleOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import ThumbUpOffAltOutlinedIcon from "@mui/icons-material/ThumbUpOffAltOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import { jwtDecode } from "jwt-decode";
import { SidebarContext } from "../Sidebar/SidebarProvider";
import {
  pageFieldSx,
  pagePrimaryButtonSx,
} from "../../styles/pageModernStyles";
import { getUserById } from "../../api/User/userApi";
import { getCustomers } from "../../api/Customer/customerApi";
import { getMarcas } from "../../api/Marcas/marcaApi";
import {
  acquireSkillFichaLock,
  createSkillFicha,
  getSkillFichaById,
  heartbeatSkillFichaLock,
  markSkillFichaDesignComplete,
  markSkillFichaReady,
  releaseSkillFichaLock,
  reopenSkillFichaDesign,
  updateSkillFicha,
} from "../../api/Skills/skillFichasApi";
import { publishSkillVersion } from "../../api/Skills/skillVersionsApi";
import {
  buildEmptyForm,
  hydrateForm,
  slugifySkill,
  toPayload,
} from "./skillFichaFormDefaults";
import { isSkillsApprover } from "./skillFichaHelpers";
import SkillFichaHelpPanel from "./SkillFichaHelpPanel";
import SkillFichaWorkflowBar from "./SkillFichaWorkflowBar";
import SkillConfirmDialog from "./SkillConfirmDialog";

const SECTIONS = [
  {
    id: "cabecera",
    title: "Datos básicos",
    subtitle: "Cliente y temporada",
    icon: AutoAwesomeOutlinedIcon,
    defaultOpen: true,
    accent: "#00897b",
  },
  {
    id: "identidad",
    title: "Identidad de marca",
    subtitle: "Quién es y para quién diseña",
    icon: PersonOutlineOutlinedIcon,
    defaultOpen: true,
    accent: "#1565c0",
  },
  {
    id: "visual",
    title: "Universo visual",
    subtitle: "Color, prints, materiales",
    icon: PaletteOutlinedIcon,
    accent: "#6a1b9a",
  },
  {
    id: "producto",
    title: "Producto / tipologías",
    subtitle: "Chaquetas y exterior",
    icon: StyleOutlinedIcon,
    defaultOpen: true,
    accent: "#ef6c00",
  },
  {
    id: "memoria",
    title: "Memoria STAGE",
    subtitle: "Refs y aprendizajes",
    icon: HistoryOutlinedIcon,
    accent: "#455a64",
  },
  {
    id: "agente",
    title: "Cómo debe trabajar el agente",
    subtitle: "Entregables y proceso",
    icon: HandshakeOutlinedIcon,
    accent: "#00838f",
  },
  {
    id: "buenos",
    title: "Buenos y malos ejemplos",
    subtitle: "Lo más importante para que acierte",
    icon: ThumbUpOffAltOutlinedIcon,
    defaultOpen: true,
    accent: "#c62828",
  },
  {
    id: "checklist",
    title: "Checklist final",
    subtitle: "Validación antes de dar por bueno",
    icon: FactCheckOutlinedIcon,
    accent: "#2e7d32",
  },
  {
    id: "cierre",
    title: "Notas finales",
    subtitle: "Comentarios y adjuntos",
    icon: NotesOutlinedIcon,
    accent: "#5d4037",
  },
];

function Section({ section, children, readOnly, index }) {
  const theme = useTheme();
  const Icon = section.icon;
  const accent = section.accent || theme.palette.primary.main;
  return (
    <Accordion
      defaultExpanded={!!section.defaultOpen}
      disableGutters
      sx={{
        mb: 2.25,
        borderRadius: "16px !important",
        overflow: "hidden",
        "&:before": { display: "none" },
        border: "1px solid",
        borderColor: alpha(accent, 0.16),
        boxShadow: `0 6px 20px ${alpha(theme.palette.common.black, 0.035)}`,
        bgcolor: theme.palette.background.paper,
        transition: "border-color 0.2s, box-shadow 0.2s",
        "&.Mui-expanded": {
          borderColor: alpha(accent, 0.32),
          boxShadow: `0 10px 28px ${alpha(accent, 0.08)}`,
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          px: { xs: 2, md: 2.5 },
          minHeight: 72,
          background: `linear-gradient(90deg, ${alpha(accent, 0.09)} 0%, ${alpha(accent, 0.02)} 52%, transparent 100%)`,
          borderLeft: `4px solid ${accent}`,
          "& .MuiAccordionSummary-content": { my: 1.35 },
        }}
      >
        <Stack direction="row" spacing={1.75} alignItems="center">
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 2.25,
              display: "grid",
              placeItems: "center",
              bgcolor: alpha(accent, 0.12),
              color: accent,
              flexShrink: 0,
            }}
          >
            <Icon fontSize="small" />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
            >
              <Typography
                fontWeight={800}
                sx={{ letterSpacing: "-0.015em", fontSize: "1.05rem" }}
              >
                {section.title}
              </Typography>
              {typeof index === "number" && (
                <Chip
                  size="small"
                  label={`${index + 1}/${SECTIONS.length}`}
                  sx={{
                    height: 22,
                    fontSize: 11,
                    fontWeight: 700,
                    bgcolor: alpha(accent, 0.12),
                    color: accent,
                  }}
                />
              )}
            </Stack>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.15, lineHeight: 1.35 }}
            >
              {section.subtitle}
              {readOnly ? " · solo lectura" : ""}
            </Typography>
          </Box>
        </Stack>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          px: { xs: 2, md: 2.75 },
          pt: 2,
          pb: { xs: 2.5, md: 3 },
          bgcolor: alpha(theme.palette.grey[50], 0.55),
          borderTop: "1px solid",
          borderColor: alpha(accent, 0.1),
        }}
      >
        <Grid container spacing={{ xs: 2, md: 2.5 }}>
          {children}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
}

/**
 * Form field with readable spacing. Long values auto-expand to multiline
 * so content is never clipped in a single-line box.
 */
function Field({
  xs = 12,
  md = 6,
  readOnly,
  multiline,
  minRows,
  maxRows = 14,
  helperText,
  sx,
  ...props
}) {
  const theme = useTheme();
  const value = props.value;
  const valueLen = String(value ?? "").length;
  const hasNewlines = /\n/.test(String(value ?? ""));
  const autoMulti = Boolean(multiline) || hasNewlines || valueLen > 72;
  const computedRows =
    minRows ??
    (autoMulti
      ? Math.min(10, Math.max(2, Math.ceil(valueLen / 55) || 2))
      : undefined);

  return (
    <Grid item xs={xs} md={md}>
      <TextField
        fullWidth
        size="medium"
        multiline={autoMulti}
        minRows={autoMulti ? computedRows : undefined}
        maxRows={autoMulti ? maxRows : undefined}
        helperText={helperText}
        FormHelperTextProps={{
          sx: {
            mx: 0.25,
            mt: 0.85,
            mb: 0.25,
            lineHeight: 1.4,
            fontSize: "0.78rem",
            color: "text.secondary",
          },
        }}
        sx={{
          ...pageFieldSx(theme),
          "& .MuiOutlinedInput-root": {
            borderRadius: 2,
            bgcolor: theme.palette.background.paper,
            alignItems: autoMulti ? "flex-start" : "center",
            fontSize: "0.9375rem",
            lineHeight: 1.5,
            color: theme.palette.text.primary,
            "& textarea": {
              overflow: "auto !important",
              resize: "vertical",
            },
          },
          "& .MuiInputLabel-root": {
            fontWeight: 600,
          },
          "& .MuiInputBase-input": {
            color: theme.palette.text.primary,
            WebkitTextFillColor: readOnly
              ? theme.palette.text.primary
              : undefined,
            opacity: readOnly ? 0.92 : 1,
          },
          "& .Mui-disabled .MuiInputBase-input": {
            WebkitTextFillColor: theme.palette.text.primary,
            opacity: 0.85,
          },
          ...sx,
        }}
        disabled={readOnly}
        {...props}
      />
    </Grid>
  );
}

export default function SkillFichaFormPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = !id || id === "new";
  const { contentMargin, isMobile } = useContext(SidebarContext);

  const [form, setForm] = useState(buildEmptyForm);
  const [meta, setMeta] = useState({});
  const [customers, setCustomers] = useState([]);
  const [marcas, setMarcas] = useState([]);
  const [sessionUser, setSessionUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");
  const [helpOpen, setHelpOpen] = useState(true);
  const [lockedByOther, setLockedByOther] = useState(null);
  const [confirmKind, setConfirmKind] = useState(null);
  const lockOwnedRef = useRef(false);

  const isMiguel = useMemo(() => isSkillsApprover(sessionUser), [sessionUser]);

  const designFinished =
    !!meta.DesignCompletedAt || form.Status === "design_done";
  const isFinalized =
    form.Status === "ready" ||
    form.Status === "skill_created" ||
    form.Status === "published";

  const readOnly =
    !!lockedByOther || isFinalized || (designFinished && !isMiguel);

  const canEdit = !readOnly && !isNew;
  const canDesignComplete =
    !isNew && canEdit && !designFinished && !lockedByOther;
  const canMarkReady =
    isMiguel &&
    designFinished &&
    form.Status !== "ready" &&
    form.Status !== "skill_created" &&
    form.Status !== "published";
  const canPublish =
    isMiguel && (form.Status === "ready" || form.Status === "skill_created");
  const canReopenPublished =
    isMiguel &&
    (form.Status === "published" ||
      form.Status === "ready" ||
      form.Status === "skill_created");
  const isPublished = form.Status === "published";

  const selectedCustomer = useMemo(
    () =>
      customers.find((c) => String(c.id) === String(form.CustomerId)) || null,
    [customers, form.CustomerId],
  );
  const selectedMarca = useMemo(
    () =>
      marcas.find((m) => String(m.ID_MARCA || m.id) === String(form.MarcaId)) ||
      null,
    [marcas, form.MarcaId],
  );

  const applyRow = useCallback((row) => {
    setForm(hydrateForm(row));
    setMeta(row || {});
  }, []);

  const loadFicha = useCallback(async () => {
    if (isNew) return;
    const row = await getSkillFichaById(id);
    applyRow(row);
    return row;
  }, [applyRow, id, isNew]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          navigate("/login");
          return;
        }
        const decoded = jwtDecode(token);
        const user = await getUserById(decoded.userId);
        if (!user?.Permiso_Skills) {
          if (!cancelled) setForbidden(true);
          return;
        }
        if (!cancelled) setSessionUser(user);

        const [custs, marks] = await Promise.all([
          getCustomers().catch(() => []),
          getMarcas().catch(() => []),
        ]);
        if (cancelled) return;
        setCustomers(Array.isArray(custs) ? custs : []);
        setMarcas(Array.isArray(marks) ? marks : []);
        await loadFicha();
      } catch (err) {
        console.error(err);
        if (err?.response?.status === 403) setForbidden(true);
        else if (!cancelled) setError("No se pudo cargar el formulario.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadFicha, navigate, isNew]);

  useEffect(() => {
    if (isNew || !id) return undefined;

    let heartbeatId;
    let cancelled = false;

    (async () => {
      try {
        const res = await acquireSkillFichaLock(id);
        if (cancelled) return;
        lockOwnedRef.current = true;
        setLockedByOther(null);
        if (res?.lockOwnerName) setMeta((m) => ({ ...m, ...res }));
        heartbeatId = window.setInterval(() => {
          heartbeatSkillFichaLock(id).catch(() => {});
        }, 60000);
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.status === 423) {
          const name = err.response?.data?.lockOwnerName || "Otra persona";
          setLockedByOther(name);
        }
      }
    })();

    return () => {
      cancelled = true;
      if (heartbeatId) window.clearInterval(heartbeatId);
      if (lockOwnedRef.current) {
        releaseSkillFichaLock(id).catch(() => {});
        lockOwnedRef.current = false;
      }
    };
  }, [id, isNew]);

  const setRoot = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const setSection = (sectionKey, field, value) => {
    setForm((prev) => ({
      ...prev,
      [sectionKey]: { ...prev[sectionKey], [field]: value },
    }));
  };

  const suggestSlug = () => {
    const base =
      form.SkillSlug ||
      [selectedCustomer?.name || form.Titulo, form.CategoriaFoco]
        .filter(Boolean)
        .join("-");
    setRoot("SkillSlug", slugifySkill(base));
  };

  const savePayload = useCallback(async () => {
    const payload = toPayload({
      ...form,
      SkillSlug:
        form.SkillSlug ||
        slugifySkill(
          [form.Titulo || selectedCustomer?.name, form.CategoriaFoco]
            .filter(Boolean)
            .join("-"),
        ),
    });
    if (!payload.Titulo?.trim()) {
      throw new Error("Pon un título (ej: Lola Casademunt · Chaquetas).");
    }
    if (isNew) {
      const created = await createSkillFicha(payload);
      navigate(`/skills/${created.id}`, { replace: true });
      return created;
    }
    return updateSkillFicha(id, payload);
  }, [form, id, isNew, navigate, selectedCustomer?.name]);

  const handleSaveDraft = async () => {
    setSaving(true);
    setError("");
    setOkMsg("");
    try {
      const updated = await savePayload();
      if (!isNew) applyRow(updated);
      setOkMsg(isNew ? "Ficha creada." : "Guardado correctamente.");
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.error || err?.message;
      if (err?.response?.status === 423 && msg) {
        setLockedByOther(err.response?.data?.lockOwnerName || "Otra persona");
      }
      setError(msg || "No se pudo guardar.");
    } finally {
      setSaving(false);
    }
  };

  const handleDesignComplete = async () => {
    setConfirmKind(null);
    setSaving(true);
    setError("");
    setOkMsg("");
    try {
      await savePayload();
      const updated = await markSkillFichaDesignComplete(
        id,
        form.CierreJson?.comentario || "",
      );
      applyRow(updated);
      lockOwnedRef.current = false;
      setOkMsg("Marcada como terminada por Diseño. Miguel puede validarla.");
    } catch (err) {
      console.error(err);
      setError(
        err?.response?.data?.error ||
          err?.message ||
          "No se pudo marcar como terminada.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleMarkReady = async () => {
    setConfirmKind(null);
    setSaving(true);
    setError("");
    try {
      const updated = await markSkillFichaReady(id);
      applyRow(updated);
      setOkMsg(
        "Lista para publicar. Usa «Publicar skill» para generar SKILL.md.",
      );
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudo marcar como lista.");
    } finally {
      setSaving(false);
    }
  };

  const handlePublishSkill = async () => {
    setConfirmKind(null);
    setSaving(true);
    setError("");
    try {
      const res = await publishSkillVersion(Number(id));
      if (res?.fichaStatus) {
        setForm((prev) => ({ ...prev, Status: res.fichaStatus }));
      }
      setOkMsg(
        `Skill publicado v${res?.version?.Version}. ${
          res.driveNote ? `Drive: ${res.driveNote}. ` : ""
        }${res.repoPath ? `Repo: ${res.repoPath}` : ""}`,
      );
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudo publicar el skill.");
    } finally {
      setSaving(false);
    }
  };

  const handleReopen = async () => {
    setConfirmKind(null);
    setSaving(true);
    try {
      const updated = await reopenSkillFichaDesign(id);
      applyRow(updated);
      setLockedByOther(null);
      setOkMsg("Ficha reabierta para Diseño.");
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudo reabrir.");
    } finally {
      setSaving(false);
    }
  };

  const confirmDialogProps = {
    designComplete: {
      title: "¿Entregar a Miguel?",
      description:
        "¿Has terminado de rellenar la parte creativa? Miguel la revisará después. Podréis reabrir la ficha más adelante si hace falta.",
      confirmLabel: "Sí, terminada por Diseño",
      cancelLabel: "Seguir editando",
      eyebrow: "Workflow · Diseño",
      tone: "secondary",
      confirmColor: "secondary",
      icon: CheckCircleOutlineIcon,
      onConfirm: handleDesignComplete,
    },
    markReady: {
      title: "¿Marcar lista para publicar?",
      description:
        "Confirmas que la ficha está validada y lista para generar el skill (SKILL.md). Después podrás pulsar «Publicar skill».",
      confirmLabel: "Sí, marcar lista",
      cancelLabel: "Cancelar",
      eyebrow: "Workflow · Validación",
      tone: "primary",
      confirmColor: "primary",
      icon: VerifiedOutlinedIcon,
      onConfirm: handleMarkReady,
    },
    publish: {
      title: "¿Publicar skill?",
      description:
        "Generará una nueva versión de SKILL.md, carpeta Drive (si está configurada) y el archivo en .cursor/skills del repo. Se puede republicar más adelante (v2, v3…).",
      confirmLabel: "Publicar ahora",
      cancelLabel: "Cancelar",
      eyebrow: "Workflow · Publicación",
      tone: "info",
      confirmColor: "info",
      icon: RocketLaunchOutlinedIcon,
      onConfirm: handlePublishSkill,
    },
    reopen: {
      title: "¿Reabrir la ficha?",
      description:
        "La ficha volverá a borrador para que se pueda editar de nuevo. El skill ya publicado no se borra; habrá que republicar tras los cambios.",
      confirmLabel: "Reabrir",
      cancelLabel: "Cancelar",
      eyebrow: "Workflow · Edición",
      tone: "warning",
      confirmColor: "warning",
      icon: ReplayOutlinedIcon,
      onConfirm: handleReopen,
    },
  }[confirmKind];

  if (forbidden) {
    return (
      <Box sx={{ ...contentMargin, p: 3 }}>
        <Alert severity="warning">
          No tienes permiso para Skills. Pide activar <b>Permiso_Skills</b>.
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box
        sx={{
          ...contentMargin,
          display: "flex",
          justifyContent: "center",
          py: 8,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const ActionBar = (
    <Stack
      direction="row"
      spacing={1}
      useFlexGap
      alignItems="center"
      flexWrap="wrap"
      sx={{
        "& .MuiButton-root": {
          height: 36,
          textTransform: "none",
          fontWeight: 700,
          px: 1.5,
          whiteSpace: "nowrap",
        },
        "& .MuiChip-root": {
          height: 36,
        },
      }}
    >
      <Button
        size="small"
        variant={helpOpen ? "contained" : "outlined"}
        startIcon={<MenuBookOutlinedIcon />}
        onClick={() => setHelpOpen((v) => !v)}
      >
        {helpOpen ? "Ocultar guía" : "Ver guía"}
      </Button>

      {!readOnly && (
        <Button
          size="small"
          variant="outlined"
          disabled={saving}
          onClick={handleSaveDraft}
          startIcon={<SaveOutlinedIcon />}
        >
          Guardar borrador
        </Button>
      )}
      {canDesignComplete && (
        <Button
          size="small"
          variant="contained"
          color="secondary"
          disabled={saving}
          onClick={() => setConfirmKind("designComplete")}
          startIcon={<CheckCircleOutlineIcon />}
        >
          Terminada por Diseño
        </Button>
      )}
      {canMarkReady && (
        <Button
          size="small"
          variant="contained"
          disabled={saving}
          sx={pagePrimaryButtonSx}
          onClick={() => setConfirmKind("markReady")}
          startIcon={<VerifiedOutlinedIcon />}
        >
          Marcar lista
        </Button>
      )}

      {/* Publicado: primero reabrir. Listo/validado: publicar. */}
      {canReopenPublished && isPublished && (
        <Button
          size="small"
          variant="contained"
          color="primary"
          disabled={saving}
          onClick={() => setConfirmKind("reopen")}
          startIcon={<ReplayOutlinedIcon />}
        >
          Reabrir para actualizar
        </Button>
      )}
      {canPublish && (
        <Button
          size="small"
          variant="contained"
          color="info"
          disabled={saving}
          onClick={() => setConfirmKind("publish")}
          startIcon={<RocketLaunchOutlinedIcon />}
        >
          Publicar skill
        </Button>
      )}
      {canReopenPublished && !isPublished && (
        <Button
          size="small"
          variant="outlined"
          disabled={saving}
          onClick={() => setConfirmKind("reopen")}
          startIcon={<ReplayOutlinedIcon />}
        >
          Reabrir
        </Button>
      )}
      {isMiguel && designFinished && !isFinalized && (
        <Button
          size="small"
          variant="outlined"
          disabled={saving}
          onClick={() => setConfirmKind("reopen")}
          startIcon={<ReplayOutlinedIcon />}
        >
          Reabrir para Diseño
        </Button>
      )}
      {readOnly && (
        <Chip
          icon={<VisibilityOutlinedIcon />}
          label="Modo lectura"
          size="small"
          variant="outlined"
          sx={{ "& .MuiChip-label": { fontWeight: 600 } }}
        />
      )}
    </Stack>
  );

  return (
    <Box
      sx={{
        boxSizing: "border-box",
        height: "100dvh",
        width: "100%",
        maxWidth: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        bgcolor: alpha(theme.palette.primary.main, 0.03),
        backgroundImage: `radial-gradient(1200px 400px at 10% -10%, ${alpha(theme.palette.primary.main, 0.1)}, transparent), radial-gradient(900px 360px at 100% 0%, ${alpha("#6a1b9a", 0.06)}, transparent)`,
        ...contentMargin,
      }}
    >
      {/* Top bar */}
      <Box
        sx={{
          flexShrink: 0,
          px: { xs: 1.5, md: 2.5 },
          py: 1.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: alpha(theme.palette.background.paper, 0.92),
          backdropFilter: "blur(10px)",
          zIndex: 3,
        }}
      >
        <Stack
          direction={{ xs: "column", lg: "row" }}
          spacing={1.5}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", lg: "center" }}
        >
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{ minWidth: 0, flex: 1 }}
          >
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate("/skills")}
              size="small"
              sx={{ flexShrink: 0, height: 36 }}
            >
              Volver
            </Button>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="h5"
                fontWeight={800}
                sx={{
                  lineHeight: 1.2,
                  letterSpacing: "-0.02em",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: { xs: "normal", md: "nowrap" },
                }}
              >
                {isNew
                  ? "Nueva ficha de cliente"
                  : form.Titulo || "Ficha de cliente"}
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
                sx={{ mt: 0.5 }}
              >
                <Typography variant="body2" color="text.secondary">
                  Briefing para que la IA diseñe como este cliente
                </Typography>
                {isPublished && (
                  <Chip
                    size="small"
                    color="info"
                    icon={<RocketLaunchOutlinedIcon />}
                    label="Skill publicado"
                  />
                )}
                {form.Status === "ready" && (
                  <Chip size="small" color="success" label="Lista para skill" />
                )}
                {designFinished &&
                  form.Status !== "ready" &&
                  form.Status !== "published" &&
                  form.Status !== "skill_created" && (
                    <Chip
                      size="small"
                      color="warning"
                      label={`Terminada · ${meta.DesignCompletedByUsername || "Diseño"}`}
                    />
                  )}
                {form.Status === "draft" && !designFinished && (
                  <Chip size="small" label="En curso" />
                )}
                {readOnly && (
                  <Chip
                    icon={<VisibilityOutlinedIcon />}
                    label="Solo lectura"
                    size="small"
                    variant="outlined"
                  />
                )}
              </Stack>
            </Box>
          </Stack>

          {!isMobile && ActionBar}
        </Stack>

        {!isNew && (
          <Box sx={{ mt: 1.5 }}>
            <SkillFichaWorkflowBar
              status={form.Status}
              designCompletedAt={meta.DesignCompletedAt}
              compact
            />
          </Box>
        )}
      </Box>

      {/* Alerts strip */}
      {(lockedByOther || (designFinished && !isMiguel) || error || okMsg) && (
        <Box sx={{ px: { xs: 1.5, md: 2.5 }, pt: 1.25, flexShrink: 0 }}>
          <Stack spacing={1}>
            {lockedByOther && (
              <Alert severity="warning" icon={<LockOutlinedIcon />}>
                <b>{lockedByOther}</b> está editando esta ficha. Puedes leerla,
                pero no guardar hasta que cierre la página.
              </Alert>
            )}
            {designFinished && !isMiguel && (
              <Alert severity="info">
                Terminada por Diseño. Si hay que cambiar algo, avisa a Miguel.
              </Alert>
            )}
            {error && (
              <Alert severity="error" onClose={() => setError("")}>
                {error}
              </Alert>
            )}
            {okMsg && (
              <Alert severity="success" onClose={() => setOkMsg("")}>
                {okMsg}
              </Alert>
            )}
          </Stack>
        </Box>
      )}

      {/* Two columns */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            lg: helpOpen ? "minmax(320px, 0.95fr) minmax(0, 1.35fr)" : "1fr",
          },
          gap: { xs: 0, lg: 2 },
          px: { xs: 0, lg: 2.5 },
          py: { xs: 0, lg: 2 },
        }}
      >
        {/* Help column */}
        {helpOpen && (
          <Box
            sx={{
              minHeight: 0,
              height: { xs: "42vh", lg: "100%" },
              order: { xs: 1, lg: 0 },
              borderBottom: { xs: "1px solid", lg: "none" },
              borderColor: "divider",
            }}
          >
            <SkillFichaHelpPanel
              open={helpOpen}
              onClose={() => setHelpOpen(false)}
              isMiguel={isMiguel}
              variant="sidebar"
            />
          </Box>
        )}

        {/* Form column */}
        <Box
          sx={{
            minHeight: 0,
            height: "100%",
            overflow: "auto",
            order: { xs: 2, lg: 0 },
            px: { xs: 1.5, lg: 0.75 },
            pb: { xs: 12, lg: 4 },
            pt: { xs: 1.5, lg: 0.5 },
            maxWidth: 980,
            mx: "auto",
            width: "100%",
            "&::-webkit-scrollbar": { width: 8 },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: alpha(theme.palette.primary.main, 0.25),
              borderRadius: 4,
            },
          }}
        >
          {!helpOpen && isMobile && (
            <Alert
              severity="info"
              sx={{ mb: 1.5 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => setHelpOpen(true)}
                >
                  Abrir guía
                </Button>
              }
            >
              ¿Dudas? Abre la guía: qué poner y en qué fijarte.
            </Alert>
          )}

          <Section section={SECTIONS[0]} readOnly={readOnly} index={0}>
            <Field
              label="Título de la ficha"
              value={form.Titulo}
              onChange={(e) => setRoot("Titulo", e.target.value)}
              placeholder="Lola Casademunt · Chaquetas & exterior"
              md={12}
              readOnly={readOnly}
            />
            <Grid item xs={12} md={6}>
              <Autocomplete
                disabled={readOnly}
                options={customers}
                getOptionLabel={(o) => o?.name || ""}
                value={selectedCustomer}
                onChange={(_, value) => setRoot("CustomerId", value?.id || "")}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Cliente"
                    size="medium"
                    sx={pageFieldSx}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                disabled={readOnly}
                options={marcas}
                getOptionLabel={(o) => o?.MARCA || o?.name || ""}
                value={selectedMarca}
                onChange={(_, value) =>
                  setRoot("MarcaId", value?.ID_MARCA || value?.id || "")
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Marca"
                    size="medium"
                    sx={pageFieldSx}
                  />
                )}
              />
            </Grid>
            <Field
              label="Categoría (ej: chaquetas, exterior…)"
              value={form.CategoriaFoco}
              onChange={(e) => setRoot("CategoriaFoco", e.target.value)}
              readOnly={readOnly}
              multiline
              minRows={2}
              md={12}
            />
            <Field
              label="Temporada"
              value={form.TemporadaRef}
              onChange={(e) => setRoot("TemporadaRef", e.target.value)}
              placeholder="SS27 / AW27"
              readOnly={readOnly}
              md={6}
            />
            <Field
              label="Código interno skill (opcional)"
              value={form.SkillSlug}
              onChange={(e) => setRoot("SkillSlug", e.target.value)}
              onBlur={suggestSlug}
              helperText="Solo si sabes lo que es; si no, déjalo vacío."
              readOnly={readOnly}
              md={6}
            />
          </Section>

          <Section section={SECTIONS[1]} readOnly={readOnly} index={1}>
            <Field
              xs={12}
              md={12}
              label="En una frase: ¿quién es esta marca?"
              value={form.IdentidadJson.frase}
              onChange={(e) =>
                setSection("IdentidadJson", "frase", e.target.value)
              }
              multiline
              minRows={3}
              readOnly={readOnly}
              helperText="Debe ser inequívoca: si quitas el nombre del cliente, ¿sigue siendo reconocible?"
            />
            <Field
              xs={12}
              md={12}
              label="Mujer / target"
              value={form.IdentidadJson.targetEdadMindset}
              onChange={(e) =>
                setSection("IdentidadJson", "targetEdadMindset", e.target.value)
              }
              multiline
              minRows={2}
              readOnly={readOnly}
              helperText="Edad mental, actitud, estilo de vida — no solo «mujer moderna»"
            />
            <Field
              xs={12}
              md={12}
              label="Cuándo usa la chaqueta"
              value={form.IdentidadJson.ocasionUso}
              onChange={(e) =>
                setSection("IdentidadJson", "ocasionUso", e.target.value)
              }
              multiline
              minRows={2}
              readOnly={readOnly}
            />
            <Field
              xs={12}
              md={6}
              label="Qué debe sentir"
              value={form.IdentidadJson.sensacionDeseada}
              onChange={(e) =>
                setSection("IdentidadJson", "sensacionDeseada", e.target.value)
              }
              multiline
              minRows={3}
              readOnly={readOnly}
            />
            <Field
              xs={12}
              md={6}
              label="Qué rechaza"
              value={form.IdentidadJson.rechaza}
              onChange={(e) =>
                setSection("IdentidadJson", "rechaza", e.target.value)
              }
              multiline
              minRows={3}
              readOnly={readOnly}
              helperText="Esto alimenta los «malos ejemplos» del agente"
            />
            <Field
              xs={12}
              md={12}
              label="Palabras SÍ (una por línea)"
              value={form.IdentidadJson.mantrasSi}
              onChange={(e) =>
                setSection("IdentidadJson", "mantrasSi", e.target.value)
              }
              multiline
              minRows={4}
              readOnly={readOnly}
              helperText="Mantras de marca: maximalismo, print, actitud…"
            />
            <Field
              xs={12}
              md={12}
              label="Palabras NO (una por línea)"
              value={form.IdentidadJson.prohibidos}
              onChange={(e) =>
                setSection("IdentidadJson", "prohibidos", e.target.value)
              }
              multiline
              minRows={4}
              readOnly={readOnly}
              helperText="Prohibidos claros: quiet luxury, beige sin firma, outdoor neutro…"
            />
            <Field
              xs={12}
              md={12}
              label="Links útiles"
              value={form.IdentidadJson.links}
              onChange={(e) =>
                setSection("IdentidadJson", "links", e.target.value)
              }
              multiline
              minRows={2}
              readOnly={readOnly}
            />
          </Section>

          <Section section={SECTIONS[2]} readOnly={readOnly} index={2}>
            <Field
              label="Colores habituales"
              value={form.UniversoVisualJson.coloresCore}
              onChange={(e) =>
                setSection("UniversoVisualJson", "coloresCore", e.target.value)
              }
              multiline
              minRows={2}
              readOnly={readOnly}
              helperText="Nombra colores concretos, no solo «vivos»"
            />
            <Field
              label="Colores de esta temporada"
              value={form.UniversoVisualJson.coloresTemporada}
              onChange={(e) =>
                setSection(
                  "UniversoVisualJson",
                  "coloresTemporada",
                  e.target.value,
                )
              }
              multiline
              minRows={2}
              readOnly={readOnly}
            />
            <Field
              label="Colores que NO usar"
              value={form.UniversoVisualJson.coloresVeto}
              onChange={(e) =>
                setSection("UniversoVisualJson", "coloresVeto", e.target.value)
              }
              multiline
              minRows={2}
              md={12}
              readOnly={readOnly}
            />
            <Field
              xs={12}
              md={12}
              label="Prints y logo"
              value={form.UniversoVisualJson.prints}
              onChange={(e) =>
                setSection("UniversoVisualJson", "prints", e.target.value)
              }
              multiline
              minRows={2}
              readOnly={readOnly}
              helperText="Qué print, intensidad y DÓNDE va en la prenda (pecho, espalda, forro…)"
            />
            <Field
              xs={12}
              md={12}
              label="Materiales SÍ"
              value={form.UniversoVisualJson.materialesSi}
              onChange={(e) =>
                setSection("UniversoVisualJson", "materialesSi", e.target.value)
              }
              multiline
              minRows={2}
              readOnly={readOnly}
              helperText="Tacto, peso, brillo si importa"
            />
            <Field
              xs={12}
              md={12}
              label="Materiales NO"
              value={form.UniversoVisualJson.materialesNo}
              onChange={(e) =>
                setSection("UniversoVisualJson", "materialesNo", e.target.value)
              }
              multiline
              minRows={2}
              readOnly={readOnly}
            />
            <Field
              xs={12}
              md={12}
              label="Detalles (cremalleras, bolsillos…)"
              value={form.UniversoVisualJson.hardwareDetalles}
              onChange={(e) =>
                setSection(
                  "UniversoVisualJson",
                  "hardwareDetalles",
                  e.target.value,
                )
              }
              multiline
              minRows={2}
              readOnly={readOnly}
              helperText="Lo que hace reconocible la chaqueta a 3 metros"
            />
          </Section>

          <Section section={SECTIONS[3]} readOnly={readOnly} index={3}>
            <Field
              xs={12}
              md={12}
              label="Tipologías a cubrir"
              value={form.ProductoJson.tipologiasMarcadas}
              onChange={(e) =>
                setSection("ProductoJson", "tipologiasMarcadas", e.target.value)
              }
              placeholder="Bomber, blazer, trench, parka…"
              multiline
              minRows={2}
              readOnly={readOnly}
              helperText="Indica también el rol: hero / core / commercial"
            />
            <Field
              xs={12}
              md={12}
              label="Detalle por tipología"
              value={form.ProductoJson.tipologiasDetalle}
              onChange={(e) =>
                setSection("ProductoJson", "tipologiasDetalle", e.target.value)
              }
              helperText="Por cada tipología clave: fit, largo, cierre, must-have, errores a evitar, 3 refs"
              multiline
              minRows={8}
              readOnly={readOnly}
            />
            <Field
              xs={12}
              md={12}
              label="Checklist «esto es la marca»"
              value={form.ProductoJson.checklistMarca}
              onChange={(e) =>
                setSection("ProductoJson", "checklistMarca", e.target.value)
              }
              multiline
              minRows={4}
              readOnly={readOnly}
              helperText="4–8 puntos que un diseño debe cumplir para pasar"
            />
          </Section>

          <Section section={SECTIONS[4]} readOnly={readOnly} index={4}>
            <Field
              xs={12}
              md={12}
              label="Aprendizajes (qué piden / rechazan)"
              value={form.MemoriaStageJson.learnings}
              onChange={(e) =>
                setSection("MemoriaStageJson", "learnings", e.target.value)
              }
              multiline
              minRows={3}
              readOnly={readOnly}
              helperText="Lo que ya sabéis de temporadas anteriores: oro puro para el agente"
            />
            <Field
              xs={12}
              md={12}
              label="Refs que el agente debe mirar siempre"
              value={form.MemoriaStageJson.refsClave}
              onChange={(e) =>
                setSection("MemoriaStageJson", "refsClave", e.target.value)
              }
              multiline
              minRows={6}
              readOnly={readOnly}
              helperText="5–8 refs + una línea de POR QUÉ cada una. Sin el «por qué», el código solo no sirve."
            />
            <Field
              xs={12}
              md={12}
              label="Producción (fábricas, MOQ…)"
              value={form.MemoriaStageJson.produccion}
              onChange={(e) =>
                setSection("MemoriaStageJson", "produccion", e.target.value)
              }
              multiline
              minRows={3}
              readOnly={readOnly}
              helperText="Evita diseños bonitos e inviables"
            />
          </Section>

          <Section section={SECTIONS[5]} readOnly={readOnly} index={5}>
            <Field
              label="Entregables"
              value={form.AgenteWorkJson.entregables}
              onChange={(e) =>
                setSection("AgenteWorkJson", "entregables", e.target.value)
              }
              multiline
              minRows={2}
              readOnly={readOnly}
            />
            <Field
              label="Formato (PDF, presentación…)"
              value={form.AgenteWorkJson.formatoSalida}
              onChange={(e) =>
                setSection("AgenteWorkJson", "formatoSalida", e.target.value)
              }
              readOnly={readOnly}
            />
          </Section>

          <Section section={SECTIONS[6]} readOnly={readOnly} index={6}>
            <Field
              xs={12}
              md={12}
              label="✅ Buenos ejemplos (mín. 3)"
              value={form.BuenosMalosJson.buenos}
              onChange={(e) =>
                setSection("BuenosMalosJson", "buenos", e.target.value)
              }
              multiline
              minRows={4}
              readOnly={readOnly}
              helperText="Ref o descripción + por qué funciona (print, fit, detalle, actitud). Bloque crítico."
            />
            <Field
              xs={12}
              md={12}
              label="❌ Malos ejemplos (mín. 3)"
              value={form.BuenosMalosJson.malos}
              onChange={(e) =>
                setSection("BuenosMalosJson", "malos", e.target.value)
              }
              multiline
              minRows={4}
              readOnly={readOnly}
              helperText="Qué parece «casi» pero NO es el cliente. Sin esto el agente no sabe qué evitar."
            />
            <Field
              xs={12}
              md={12}
              label="Frase de oro (1 regla que no se olvida)"
              value={form.BuenosMalosJson.fraseOro}
              onChange={(e) =>
                setSection("BuenosMalosJson", "fraseOro", e.target.value)
              }
              multiline
              minRows={2}
              readOnly={readOnly}
              helperText="Ej: «Si no se reconoce a 3 metros, no es Lola». Si falla esta regla, el diseño se tira."
            />
          </Section>

          <Section section={SECTIONS[7]} readOnly={readOnly} index={7}>
            <Field
              xs={12}
              md={12}
              label="Checklist (una línea = un punto)"
              value={form.ChecklistJson.items}
              onChange={(e) =>
                setSection("ChecklistJson", "items", e.target.value)
              }
              multiline
              minRows={5}
              readOnly={readOnly}
            />
          </Section>

          <Section section={SECTIONS[8]} readOnly={readOnly} index={8}>
            <Field
              xs={12}
              md={12}
              label="Comentario para Miguel"
              value={form.CierreJson.comentario}
              onChange={(e) =>
                setSection("CierreJson", "comentario", e.target.value)
              }
              multiline
              minRows={2}
              readOnly={readOnly}
            />
            <Field
              xs={12}
              md={12}
              label="Links a adjuntos (Drive, fotos…)"
              value={form.CierreJson.adjuntos}
              onChange={(e) =>
                setSection("CierreJson", "adjuntos", e.target.value)
              }
              multiline
              minRows={2}
              readOnly={readOnly}
            />
          </Section>
        </Box>
      </Box>

      {isMobile && (
        <Paper
          elevation={10}
          sx={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
            p: 1.5,
            zIndex: theme.zIndex.appBar,
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: alpha(theme.palette.background.paper, 0.96),
            backdropFilter: "blur(8px)",
          }}
        >
          {ActionBar}
        </Paper>
      )}

      <SkillConfirmDialog
        open={Boolean(confirmKind && confirmDialogProps)}
        onClose={() => setConfirmKind(null)}
        confirming={saving}
        {...(confirmDialogProps || {
          title: "",
          description: "",
          onConfirm: () => setConfirmKind(null),
        })}
      />
    </Box>
  );
}
